import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import SplashScreen from './SplashScreen';

/**
 * SplashManager - Controls when to show the splash screen
 * Now features fluid blob animations in the business color
 */
const SplashManager = ({ blueprintName = 'BlueLink', mainColor }) => {
  // FOR TESTING: Set to true to force the splash screen to show on every page load
  const FORCE_SPLASH = true;

  const [showSplash, setShowSplash] = useState(FORCE_SPLASH);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionDurationRef = useRef(1200); // Default 1200ms (1.2s)
  const location = useLocation();

  // Get the blueprint ID from URL
  const blueprintId = location.pathname.split('/').pop();

  useEffect(() => {
    // Get the transition duration from CSS
    try {
      const durationStr = getComputedStyle(document.documentElement)
        .getPropertyValue('--transition-duration')
        .trim();
      if (durationStr) {
        // Convert from 's' to ms (e.g., '1.2s' to 1200)
        const durationInSeconds = parseFloat(durationStr);
        if (!isNaN(durationInSeconds)) {
          transitionDurationRef.current = durationInSeconds * 1000;
        }
      }
    } catch (e) {
      // Keep default if there's an error
    }

    if (FORCE_SPLASH) {
      setShowSplash(true);
      return;
    }

    // Check if user has seen this blueprint's splash
    const storageKey = `bluelinkSplashSeen_${blueprintId}`;
    const hasSeenSplash = localStorage.getItem(storageKey) === 'true';

    if (!hasSeenSplash) {
      setShowSplash(true);
    }
  }, [blueprintId, FORCE_SPLASH]);

  const handleSplashComplete = () => {
    // Mark that we're in the transition state
    setIsTransitioning(true);

    // When user clicks to continue, mark as seen and hide splash
    const storageKey = `bluelinkSplashSeen_${blueprintId}`;
    localStorage.setItem(storageKey, 'true');
    localStorage.setItem('bluelinkSplashSeen', 'true');

    // Allow the full animation sequence to complete before removing the component
    // Total animation time = zoom duration + fade duration
    setTimeout(() => {
      setShowSplash(false);
      setIsTransitioning(false);
    }, transitionDurationRef.current * 2); // Account for both zoom and fade transitions
  };

  // Get business color from props with fallback
  const getBusinessColor = () => {
    // First check if we have a mainColor prop
    if (mainColor) {
      // Ensure color has # prefix if it's a hex code without '#'
      if (!mainColor.startsWith('#') && /^[0-9A-Fa-f]{6}$/.test(mainColor)) {
        return `#${mainColor}`;
      }
      return mainColor;
    }

    // Fallback to CSS variables
    try {
      // Try to get from CSS variable first
      let color = getComputedStyle(document.documentElement)
        .getPropertyValue('--businessColor')
        .trim();

      // If not found or empty, check for other common business color variables
      if (!color) {
        color = getComputedStyle(document.documentElement)
          .getPropertyValue('--primary-color')
          .trim();
      }

      if (!color) {
        color = getComputedStyle(document.documentElement)
          .getPropertyValue('--brand-color')
          .trim();
      }

      if (!color) {
        return '#007AFF'; // Default to blue if no color is found
      }

      return color;
    } catch (e) {
      return '#007AFF'; // Fallback color
    }
  };

  if (!showSplash) {
    return null;
  }

  // Make sure we have a valid business color
  const businessColor = getBusinessColor();

  // Set the CSS variable directly here as well, for redundancy
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty(
      '--businessColor',
      businessColor
    );
    document.documentElement.style.setProperty('--target-color', businessColor);
  }

  return (
    <SplashScreen
      onComplete={handleSplashComplete}
      businessColor={businessColor}
      blueprintName={blueprintName}
      forceShow={FORCE_SPLASH}
    />
  );
};

export default SplashManager;
